import { onIdTokenChanged, signOut as firebaseSignOut, type User } from 'firebase/auth'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { configureApiClient } from '@/shared/api/client'
import { isDevAuthMode, isMockMode } from '@/shared/config/runtime-config'

import { getFirebaseAuth } from './firebase'
import { tokenManager } from './token-manager'

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export interface AuthContextValue {
  status: AuthStatus
  user: User | null
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<User | null>(null)
  const signingOut = useRef(false)

  const signOut = useCallback(async () => {
    if (signingOut.current) return
    signingOut.current = true
    try {
      const auth = getFirebaseAuth()
      if (auth) await firebaseSignOut(auth)
    } finally {
      tokenManager.clear()
      // En una app multicompany, dejar cache de la sesion anterior es una fuga
      // entre usuarios. Se limpia TODA, no solo las keys del usuario.
      queryClient.clear()
      signingOut.current = false
    }
  }, [queryClient])

  useEffect(() => {
    const auth = getFirebaseAuth()

    if (!auth) {
      // Sin Firebase configurado (dev sin claves, tests): no hay sesion posible,
      // pero la app tiene que llegar a `/login` en vez de quedar en `loading`.
      //
      // En modo mock damos la sesion por iniciada y `GET /me` (que contesta
      // MSW) decide el rol. Es la unica forma de mirar las pantallas internas
      // sin backend, y sigue siendo `/me` quien manda: el rol NUNCA sale de un
      // token, tampoco aca.
      // En modo dev-auth la identidad viaja en `X-Dev-User` y no hay Firebase:
      // sin esto el arbol entero queda `anonymous` y nadie pasa de `/login`.
      setStatus(isMockMode() || isDevAuthMode() ? 'authenticated' : 'anonymous')
      return
    }

    // `onIdTokenChanged` y no `onAuthStateChanged`: el primero tambien dispara
    // en cada refresh del token, que es justo cuando hay que reavisarle al
    // token manager.
    return onIdTokenChanged(auth, (nextUser) => {
      tokenManager.setUser(nextUser)
      setUser(nextUser)
      setStatus(nextUser ? 'authenticated' : 'anonymous')
    })
  }, [])

  useEffect(() => {
    configureApiClient({
      getToken: (forceRefresh) => tokenManager.getToken(forceRefresh),
      onAuthFailure: () => signOut(),
    })
  }, [signOut])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, signOut }),
    [status, user, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth() necesita estar dentro de <AuthProvider>')
  return context
}
